import React, { useState, useEffect } from 'react';
import { ArrowUpRight, ChevronLeft, ChevronRight, Star } from 'lucide-react';
import ClientMarquee from '../common/ClientCarousel';

export default function Testimonial() {
  const [isVisible, setIsVisible] = useState(false);
  const [currentTestimonial, setCurrentTestimonial] = useState(0);

  useEffect(() => {
    setIsVisible(true);
  }, []);

  const testimonials = [
    {
      id: 1,
      quote: "Their consulting team reshaped our strategy with clarity and precision. Within months, we saw measurable growth, stronger market positioning, and increased team confidence in execution.",
      author: "James Whitaker",
      role: "CEO, Finex Solutions",
      avatar: "https://media.istockphoto.com/id/1277971635/photo/portrait-of-a-smiling-man-of-indian-ethnicity.jpg?s=2048x2048&w=is&k=20&c=CaYXzvIwCETYjay_jU5IWjiR6eNLw4urUfcq4y5vEqA=",
      rating: 5
    },
    {
      id: 2,
      quote: "The transformation was remarkable. Their expertise in digital solutions helped us modernize our infrastructure and improve operational efficiency by 60%. Truly exceptional partnership.",
      author: "Sarah Chen",
      role: "CTO, TechVision Inc",
      avatar: "https://media.istockphoto.com/id/1388253782/photo/positive-successful-millennial-business-professional-man-head-shot-portrait.jpg?s=2048x2048&w=is&k=20&c=0HU1oQGXlO4nrrMKKzAK4Jmu6XDLvXhTGjKScvrNIZw=",
      rating: 5
    },
    {
      id: 3,
      quote: "Outstanding results! Their strategic approach to cybersecurity not only protected our assets but also gave us the confidence to expand into new markets. Highly recommended.",
      author: "Michael Rodriguez",
      role: "Director, GlobalTech",
      avatar: "https://media.istockphoto.com/id/1317804578/photo/one-businesswoman-headshot-smiling-at-the-camera.webp?a=1&b=1&s=612x612&w=0&k=20&c=0ez7MLSYsOlKDghpVAUDMVf_gT5B_DfKmRNKqElOLUA=",
      rating: 5
    }
  ];

  const nextTestimonial = () => {
    setCurrentTestimonial((prev) => (prev + 1) % testimonials.length);
  };

  const prevTestimonial = () => {
    setCurrentTestimonial((prev) => (prev - 1 + testimonials.length) % testimonials.length);
  };

  const current = testimonials[currentTestimonial];

  return (
    <div className=" bg-gradient-to-br from-slate-900 via-blue-950 to-slate-900 py-20 px-6 relative overflow-hidden">
      {/* Grid Pattern Background */}
      <div className="absolute inset-0 bg-[linear-gradient(rgba(255,255,255,0.03)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.03)_1px,transparent_1px)] bg-[size:64px_64px]"></div>

      {/* Decorative stars in corners */}
      <div className="absolute top-4 left-4">
        <svg width="24" height="24" viewBox="0 0 24 24" className="text-slate-600">
          <path d="M12 0l3 9l9 3l-9 3l-3 9l-3-9l-9-3l9-3z" fill="currentColor" opacity="0.3" />
        </svg>
      </div>
      <div className="absolute top-4 right-4">
        <svg width="24" height="24" viewBox="0 0 24 24" className="text-slate-600">
          <path d="M12 0l3 9l9 3l-9 3l-3 9l-3-9l-9-3l9-3z" fill="currentColor" opacity="0.3" />
        </svg>
      </div>
      <div className="absolute bottom-20 left-4">
        <svg width="24" height="24" viewBox="0 0 24 24" className="text-slate-600">
          <path d="M12 0l3 9l9 3l-9 3l-3 9l-3-9l-9-3l9-3z" fill="currentColor" opacity="0.3" />
        </svg>
      </div>
      <div className="absolute bottom-20 right-4">
        <svg width="24" height="24" viewBox="0 0 24 24" className="text-slate-600">
          <path d="M12 0l3 9l9 3l-9 3l-3 9l-3-9l-9-3l9-3z" fill="currentColor" opacity="0.3" />
        </svg>
      </div>

      <div className="container mx-auto max-w-7xl relative z-10">
        {/* Header */}
        <div className="text-center space-y-8 mb-16">
          {/* Badge */}
          <div 
            className={`inline-flex items-center gap-3 px-8 py-4 bg-blue-900/40 backdrop-blur-sm rounded-full border border-blue-500/30 transition-all duration-1000 ${
              isVisible ? 'opacity-100 scale-100' : 'opacity-0 scale-90'
            }`}
          >
            <div className="w-2 h-2 bg-blue-400 rounded-sm"></div>
            <span className="text-blue-100 font-medium">What Our Clients Say</span>
            <div className="w-2 h-2 bg-blue-400 rounded-sm"></div>
          </div>

          {/* Heading */}
          <div className="space-y-2 md:space-y-4">
            <h2 
              className={`text-3xl sm:text-4xl lg:text-5xl font-bold text-white leading-tight transition-all duration-1000 delay-200 ${
                isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
              }`}
            >
              <span className="italic">Building Confidence</span> Through IT Success
            </h2>
          </div>

          {/* Description */}
          <p 
            className={`text-slate-300 text-base md:text-lg max-w-3xl mx-auto leading-relaxed transition-all duration-1000 delay-400 ${
              isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
            }`}
          >
            Explore authentic client stories showing how our expertise and innovation transform challenges into opportunities.
          </p>
        </div>

        {/* Testimonial Content */}
        <div 
          className={`grid lg:grid-cols-2 gap-12 items-center mb-20 transition-all duration-1000 delay-600 ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'
          }`}
        >
          {/* Left Side */}
          <div className="space-y-8">
            {/* Client Avatars */}
            <div className="flex items-center gap-4">
              <div className="flex -space-x-4">
                <div className="size-12 md:size-16">
                   <img src="https://media.istockphoto.com/id/1388253782/photo/positive-successful-millennial-business-professional-man-head-shot-portrait.jpg?s=2048x2048&w=is&k=20&c=0HU1oQGXlO4nrrMKKzAK4Jmu6XDLvXhTGjKScvrNIZw=" alt="" className='w-full h-full rounded-full object-cover' />
                </div>
                <div className="size-12 md:size-16">
                   <img src="https://media.istockphoto.com/id/1277971635/photo/portrait-of-a-smiling-man-of-indian-ethnicity.jpg?s=2048x2048&w=is&k=20&c=CaYXzvIwCETYjay_jU5IWjiR6eNLw4urUfcq4y5vEqA=" alt="" className='w-full h-full rounded-full object-cover' />
                </div>
                <div className="size-12 md:size-16 ">
                   <img src="https://media.istockphoto.com/id/1317804578/photo/one-businesswoman-headshot-smiling-at-the-camera.webp?a=1&b=1&s=612x612&w=0&k=20&c=0ez7MLSYsOlKDghpVAUDMVf_gT5B_DfKmRNKqElOLUA=" alt="" className='w-full h-full rounded-full object-cover' />
                </div>
              </div>
              <div className="size-12 md:size-16 rounded-full bg-gradient-to-br from-purple-500 to-blue-600 flex items-center justify-center">
                <svg width="32" height="32" viewBox="0 0 32 32" className="text-white">
                  <path d="M8 12h4v8H8V12zm12 0h4v8h-4V12z" fill="currentColor" />
                </svg>
              </div>
            </div>

            {/* Stats */}
            <div>
              <h3 className="text-xl md:text-3xl font-bold text-white mb-2">
                Trusted by 500+ clients
              </h3>
              <p className="text-slate-400 text-base md:text-lg">
                across industries worldwide.
              </p>
            </div>

            {/* Button */}
            <div>
              <button className="group px-8 py-4 bg-white text-slate-900 rounded-xl font-medium hover:bg-slate-100 transition-all duration-300 hover:scale-105 hover:shadow-2xl flex items-center gap-3">
                View All Testimonials
                <ArrowUpRight className="w-5 h-5 group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" />
              </button>
            </div>
          </div>

          {/* Right Side - Testimonial Card */}
          <div className="relative">
            <div className="bg-gradient-to-br from-slate-50 to-slate-100 rounded-3xl p-10 shadow-2xl">
              {/* Quote */}
              <div className="mb-8">
                <p className="text-slate-900 text-lg md:text-xl leading-relaxed">
                  "{current.quote}"
                </p>
              </div>

              {/* Divider */}
              <div className="w-16 h-[2px] bg-slate-300 mb-8"></div>

              {/* Author Info */}
              <div className="flex flex-col md:flex-row gap-4 items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="size-16 md:size-20 rounded-full bg-gradient-to-br from-purple-400 to-blue-500 flex items-center justify-center text-white font-bold text-xl">
                    <img src={current.avatar} alt="" className='h-full w-full rounded-full object-cover' />
                    
                  </div>
                  <div>
                    <h4 className="text-slate-900 font-bold text-lg">{current.author}</h4>
                    <p className="text-slate-600 italic">{current.role}</p>
                    <div className="flex gap-1 mt-1">
                      {[...Array(current.rating)].map((_, i) => (
                        <Star key={i} className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                      ))}
                    </div>
                  </div>
                </div>

                {/* Navigation Arrows */}
                <div className="flex gap-2">
                  <button 
                    onClick={prevTestimonial}
                    className="w-12 h-12 bg-gradient-to-br from-purple-500 to-blue-500 rounded-full flex items-center justify-center text-white hover:scale-110 transition-all"
                  >
                    <ChevronLeft className="w-6 h-6" />
                  </button>
                  <button 
                    onClick={nextTestimonial}
                    className="w-12 h-12 bg-gradient-to-br from-purple-500 to-blue-500 rounded-full flex items-center justify-center text-white hover:scale-110 transition-all"
                  >
                    <ChevronRight className="w-6 h-6" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Logo Carousel */}
        <div className="relative overflow-hidden">
         <ClientMarquee/>
        </div>
      </div>

      <style>{`
        @keyframes scroll-infinite {
          0% {
            transform: translateX(0);
          }
          100% {
            transform: translateX(-50%);
          }
        }

        .animate-scroll-infinite {
          animation: scroll-infinite 30s linear infinite;
        }

        .animate-scroll-infinite:hover {
          animation-play-state: paused;
        }
      `}</style>
    </div>
  );
}